import React, { useState, useEffect, useRef, useCallback } from "react";
import { inject, observer } from "mobx-react";
import { Row, Col, Tooltip } from "antd";
import ContentDrawer from "components/ContentDrawer/ContentDrawer";
import FormSelectInput from "components/FormItems/FormSelectInput";
import TimeZoneSelector from "components/FormItems/TimeZoneSelector";
import moment from "moment";
import TabSection from "./TabSection";
import FilterSection from "./FilterSection";
import DataTableSection from "./DataTableSection";
import { refreshOptions } from "constant/commonList";

const ScheduledDashboard = ({ gatewayBulkStore, gatewayMmsStore, messageStore, authStore }) => {
  const { user } = authStore;

  // UI State
  const [loading, setLoading] = useState(false);
  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [tabname, setTabName] = useState("sms");
  const [scheduleType, setScheduleType] = useState("onetime");
  const [statusFilter, setStatusFilter] = useState("all");

  // Timezone State - Separate states for display and actual timezone
  const [displayTimezone, setDisplayTimezone] = useState(() => {
    const savedTimezone = localStorage.getItem("scheduledDashboardTimezone");
    return savedTimezone || user?.userTimeZone || moment.tz.guess();
  });

  const [userTimeZone, setUserTimeZone] = useState(() => {
    const savedTimezone = localStorage.getItem("scheduledDashboardTimezone");
    return savedTimezone || user?.userTimeZone || moment.tz.guess();
  });

  // Auto-refresh State
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(() => {
    if (localStorage.getItem("scheduledAutoRefreshInterval")) {
      return parseInt(localStorage.getItem("scheduledAutoRefreshInterval"));
    } else {
      return refreshOptions[3].value;
    }
  });
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const intervalRef = useRef(null);
  const timezoneChangeTimeoutRef = useRef(null);

  // Get current store based on tab
  const currentStore = tabname === "mms" ? gatewayMmsStore : gatewayBulkStore;
  const { selectBulk, selectedBulk, bulkList, total, pageSize, current, setPage, setPageSize } = currentStore;

  // Fetch data based on tab, schedule type, and status
  const fetchScheduledData = useCallback(async () => {
    try {
      setLoading(true);

      if (tabname === "mms") {
        // MMS API calls based on schedule type
        if (scheduleType === "onetime") {
          await gatewayMmsStore.getOneTimeScheduleMMSBulkList();
        } else if (scheduleType === "recurring") {
          await gatewayMmsStore.getRecurringSchedule();
        } else {
          await gatewayMmsStore.getScheduledMMSBulkList();
        }
      } else {
        // SMS API calls based on schedule type
        if (scheduleType === "onetime") {
          await gatewayBulkStore.getOneTimeScheduleSMSBulkList();
        } else if (scheduleType === "recurring") {
          await gatewayBulkStore.getRecurringSchedule();
        } else {
          await gatewayBulkStore.getScheduledSMSBulkList();
        }
      }
    } catch (error) {
      console.error("Error fetching scheduled data:", error);
    } finally {
      setLoading(false);
    }
  }, [tabname, scheduleType, statusFilter, current, pageSize, gatewayBulkStore, gatewayMmsStore]);

  // Auto-refresh effect
  useEffect(() => {
    if (isAutoRefreshEnabled && autoRefreshInterval > 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        fetchScheduledData();
      }, autoRefreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefreshInterval, isAutoRefreshEnabled, fetchScheduledData]);

  // Initial data fetch
  useEffect(() => {
    fetchScheduledData();
  }, [tabname, scheduleType, statusFilter, current, pageSize]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timezoneChangeTimeoutRef.current) {
        clearTimeout(timezoneChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleAutoRefreshChange = value => {
    localStorage.setItem("scheduledAutoRefreshInterval", value);
    setAutoRefreshInterval(value);
  };

  // Handle timezone change with debounce
  const handleTimezoneChange = timezone => {
    // Update display immediately for smooth UX
    setDisplayTimezone(timezone);

    // Clear existing timeout
    if (timezoneChangeTimeoutRef.current) {
      clearTimeout(timezoneChangeTimeoutRef.current);
    }

    // Debounce the actual timezone change to avoid multiple re-renders
    timezoneChangeTimeoutRef.current = setTimeout(() => {
      setUserTimeZone(timezone);
      localStorage.setItem("scheduledDashboardTimezone", timezone);
    }, 500); // Apply change after 500ms of no activity
  };

  const handleManualRefresh = () => {
    setPage(1);
    setStatusFilter("all");
    currentStore.setStatus("all");
    fetchScheduledData();
  };

  const handleTabChange = key => {
    setTabName(key);
    currentStore.setSearch("");
    setPage(1);
    setStatusFilter("all");
    setScheduleType("all");
  };

  const handleScheduleTypeChange = key => {
    setScheduleType(key);
    setPage(1);
    // Reset status filter to "all" for one-time schedules if it's "running"
    if (key === "onetime" && statusFilter === "running") {
      setStatusFilter("all");
      currentStore.setStatus("all");
    }
  };

  const handleFilterChange = value => {
    const newStatus = value === statusFilter ? "all" : value;
    setStatusFilter(newStatus);
    currentStore.setStatus(newStatus);
    setPage(1);
  };

  const handleClick = (key, record) => {
    switch (key) {
      case "view":
        if (record?.sender) {
          messageStore.setPhoneSender(record.sender);
        }
        selectBulk(record);
        setVisibleDrawer(true);
        break;
      case "cancel":
        selectBulk(record);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Row gutter={10} className="p-2">
        <Col span={24} className="mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <TabSection tabname={tabname} scheduleType={scheduleType} onTabChange={handleTabChange} onScheduleTypeChange={handleScheduleTypeChange} />
            <div className="d-flex flex-column align-items-end gap-2">
              <FilterSection statusFilter={statusFilter} scheduleType={scheduleType} onFilterChange={handleFilterChange} />
              <div className="d-flex align-items-center gap-2 h-4">
                <div className="mt-4" style={{ minWidth: "320px" }}>
                  <TimeZoneSelector values={{ userTimeZone: displayTimezone }} setFieldValue={(field, value) => handleTimezoneChange(value)} />
                </div>

                <FormSelectInput label={null} value={autoRefreshInterval} onChange={handleAutoRefreshChange} options={refreshOptions} size="small" placeholder="Select interval" secondary />

                <Tooltip title="Refresh">
                  <i className="fas fa-sync-alt text-info-color fs-5" type="button" onClick={handleManualRefresh} style={{ cursor: "pointer" }} />
                </Tooltip>

                <Tooltip title={isAutoRefreshEnabled ? "Disable Auto Refresh" : "Enable Auto Refresh"}>
                  <i
                    className={`fas ${isAutoRefreshEnabled ? "fa-pause" : "fa-play"} text-info-color fs-5`}
                    type="button"
                    onClick={() => setIsAutoRefreshEnabled(!isAutoRefreshEnabled)}
                    style={{ cursor: "pointer" }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Col>

        <Col span={24}>
          <DataTableSection
            loading={loading}
            dataSource={bulkList}
            current={current}
            total={total}
            pageSize={pageSize}
            tabname={tabname}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={handleClick}
            userTimeZone={userTimeZone}
          />
        </Col>
      </Row>

      <ContentDrawer
        headerName={messageStore.phoneFrameSender}
        sender={selectedBulk?.sender || messageStore.phoneFrameSender}
        visible={visibleDrawer}
        handleClose={() => setVisibleDrawer(false)}
        refreshCampaignData={fetchScheduledData}
        isMms={tabname === "mms"}
        selectedData={selectedBulk}
        handleActions={handleClick}
        userTimeZone={userTimeZone}
        allowedit={false}
        showExecutedAtButton={true}
      />
    </>
  );
};

export default inject(stores => ({
  gatewayBulkStore: stores.store.gatewayBulkStore,
  gatewayMmsStore: stores.store.gatewayMmsStore,
  messageStore: stores.store.messageStore,
  authStore: stores.store.authStore
}))(observer(ScheduledDashboard));
